/**
 * THE calendar-day representation for the calendar page.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A calendar day is a `Date` at that day's MIDNIGHT IN THE PROFILE TIMEZONE.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every `Date` that stands for a day on this page — `currentDate`, a month
 * grid cell, a week column, a fetch-range boundary — is that instant, and
 * nothing else. There is no second representation and no hybrid: a
 * `'YYYY-MM-DD'` string is what a day is *keyed* and *displayed* by, and this
 * module is the only place the two forms convert into each other.
 *
 * Two consequences that are the whole point:
 *
 * 1. **Read a day's parts only through this module** (or `Intl` with an
 *    explicit `timeZone: timezone`). `date.getDate()` / `getMonth()` /
 *    `getDay()` read the BROWSER's calendar, which is a different day for
 *    anyone whose profile timezone isn't their laptop's — that divergence is
 *    the bug this representation exists to remove.
 * 2. **A day boundary is a real instant**, so `eventStart < dayEnd &&
 *    eventEnd > dayStart` buckets an event into the day the user would name,
 *    and `calculateEventPosition(..., day, timezone)` positions it at the
 *    hour the user would read off a clock.
 *
 * All arithmetic (next day, week start, month grid) runs as CIVIL arithmetic
 * on `'YYYY-MM-DD'` — never by adding milliseconds — so a 23- or 25-hour DST
 * day still advances exactly one calendar day.
 */

import {
  addCalendarDaysToYmd,
  calendarDateIn,
  startOfCalendarDayIn,
  todayIn,
} from '@/lib/time';

/** The `'YYYY-MM-DD'` key of a calendar day. The page's map/URL/DOM key. */
export function calendarDayKey(day: Date, timezone: string): string {
  return calendarDateIn(day, timezone);
}

/** The calendar day a `'YYYY-MM-DD'` key names, as a profile-tz midnight. */
export function calendarDayFromKey(key: string, timezone: string): Date {
  return startOfCalendarDayIn(key, timezone);
}

/** The calendar day `instant` falls on, as a profile-tz midnight. */
export function calendarDayOf(instant: Date, timezone: string): Date {
  return startOfCalendarDayIn(calendarDateIn(instant, timezone), timezone);
}

/** Today's calendar day in `timezone`, as a profile-tz midnight. */
export function todayCalendarDay(
  timezone: string,
  now: Date = new Date()
): Date {
  return startOfCalendarDayIn(todayIn(timezone, now), timezone);
}

/** `day` shifted by whole calendar days (negative shifts backwards). */
export function addCalendarDays(
  day: Date,
  days: number,
  timezone: string
): Date {
  return startOfCalendarDayIn(
    addCalendarDaysToYmd(calendarDateIn(day, timezone), days),
    timezone
  );
}

/** A calendar day's numeric parts (`month` is 1-12). */
export function calendarDayParts(
  day: Date,
  timezone: string
): { year: number; month: number; day: number } {
  const [year, month, dayOfMonth] = calendarDateIn(day, timezone)
    .split('-')
    .map(Number);
  return { year, month, day: dayOfMonth };
}

/** Day of week, 0 = Sunday … 6 = Saturday, in `timezone`. */
export function calendarDayOfWeek(day: Date, timezone: string): number {
  const { year, month, day: dayOfMonth } = calendarDayParts(day, timezone);
  return new Date(Date.UTC(year, month - 1, dayOfMonth)).getUTCDay();
}

/** The half-open instant range `[start, end)` a calendar day occupies. */
export function calendarDayBounds(
  day: Date,
  timezone: string
): { dayStart: Date; dayEnd: Date } {
  return { dayStart: day, dayEnd: addCalendarDays(day, 1, timezone) };
}

/** Whole calendar days from `earlier` to `later` (negative when reversed). */
export function daysBetweenCalendarDays(
  earlier: Date,
  later: Date,
  timezone: string
): number {
  const a = calendarDayParts(earlier, timezone);
  const b = calendarDayParts(later, timezone);
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) -
      Date.UTC(a.year, a.month - 1, a.day)) /
      86_400_000
  );
}

/** First day of the week containing `day`, honoring the locale's week start. */
export function startOfCalendarWeek(
  day: Date,
  weekStartDay: number,
  timezone: string
): Date {
  const daysFromStart =
    (calendarDayOfWeek(day, timezone) - weekStartDay + 7) % 7;
  return addCalendarDays(day, -daysFromStart, timezone);
}

/** The 1st of the month containing `day`. */
export function startOfCalendarMonth(day: Date, timezone: string): Date {
  const { year, month } = calendarDayParts(day, timezone);
  return startOfCalendarDayIn(
    `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`,
    timezone
  );
}

/**
 * The 1st of the month `months` away from `day`'s month. Anchoring on the 1st
 * is what keeps month navigation from overflowing (Jan 31 + 1 month is Mar 3
 * if you keep the day-of-month; here it is Feb 1).
 */
export function addCalendarMonths(
  day: Date,
  months: number,
  timezone: string
): Date {
  const { year, month } = calendarDayParts(day, timezone);
  const shifted = new Date(Date.UTC(year, month - 1 + months, 1));
  return startOfCalendarDayIn(calendarDateIn(shifted, 'UTC'), timezone);
}

/** The last day of the month containing `day`. */
export function lastDayOfCalendarMonth(day: Date, timezone: string): Date {
  return addCalendarDays(addCalendarMonths(day, 1, timezone), -1, timezone);
}

/** Number of days in the month containing `day`. */
export function daysInCalendarMonth(day: Date, timezone: string): number {
  return calendarDayParts(lastDayOfCalendarMonth(day, timezone), timezone).day;
}

/**
 * The days of the month-view grid: complete weeks covering every day of
 * `day`'s month, including the leading/trailing days shared with the
 * neighbouring months (4–6 rows of 7).
 *
 * `getMonthGridRange` in useCalendarData fetches exactly this span, so the two
 * must stay in step — both derive it from here.
 */
export function monthGridDays(
  day: Date,
  weekStartDay: number,
  timezone: string
): Date[] {
  const firstOfMonth = startOfCalendarMonth(day, timezone);
  const daysBeforeMonth =
    (calendarDayOfWeek(firstOfMonth, timezone) - weekStartDay + 7) % 7;
  const totalCells =
    Math.ceil((daysBeforeMonth + daysInCalendarMonth(day, timezone)) / 7) * 7;

  // One conversion per cell rather than per lookup: walk the civil days and
  // resolve each to its profile-tz midnight once.
  const firstCellYmd = addCalendarDaysToYmd(
    calendarDateIn(firstOfMonth, timezone),
    -daysBeforeMonth
  );
  const days: Date[] = [];
  for (let i = 0; i < totalCells; i++) {
    days.push(
      startOfCalendarDayIn(addCalendarDaysToYmd(firstCellYmd, i), timezone)
    );
  }
  return days;
}

/**
 * The civil `'YYYY-MM-DD'` range an ALL-DAY event covers.
 *
 * All-day events are calendar dates, not instants: `eventMapper` brackets them
 * as `'<startDate>T00:00:00'` / `'<endDate>T23:59:59'` with no zone, so the
 * date half IS the event. Comparing those dates as strings keeps an all-day
 * event on exactly the days it names in every timezone — parsing them into
 * instants would smear a one-day event across two columns for any profile
 * timezone that isn't the browser's.
 */
export function allDayEventDateRange(event: {
  start_time: string;
  end_time: string;
}): { startYmd: string; endYmd: string } | null {
  const startYmd = event.start_time.slice(0, 10);
  const endYmd = event.end_time.slice(0, 10);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(startYmd) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(endYmd)
  ) {
    return null;
  }
  return { startYmd, endYmd: endYmd < startYmd ? startYmd : endYmd };
}
